import { Component, inject, signal } from '@angular/core';
import { SharedModule } from '../../shared/shared.module';
import { Router } from '@angular/router';
import { CareerService } from './services/career.service';
import { finalize, takeUntil } from 'rxjs';
import { InfoCardComponent } from '@apps/landing/src/app/shared/components/cards/info-card/info-card.component';
import { CompoundCardComponent } from '@apps/landing/src/app/shared/components/cards/compound-card/compound-card.component';
import { handleResponseMessage, RESPONSE_TYPE } from '@apps/landing/src/app/shared/utils/handle-response-message';
import { HlmSkeletonComponent } from '@spartan-ng/ui-skeleton-helm';
import { colorMap, orderMap } from './utils/badge-statuses';
import { ResponseModel } from '@apps/landing/src/app/core/models/response.model';
import { ICareerOfferModel } from '@apps/landing/src/app/modules/careers/models/careeer.model';
import { PAGE_ROUTES } from '@apps/landing/src/app/shared/constants/routes.enum';
import { Unsubscribe } from '@apps/landing/src/app/shared/classes/unsubscribe';
import { NgOptimizedImage } from '@angular/common';

@Component({
  selector: 'app-careers',
  imports: [SharedModule, InfoCardComponent, CompoundCardComponent, HlmSkeletonComponent, NgOptimizedImage],
  standalone: true,
  templateUrl: './careers.component.html',
})
export class CareersComponent extends Unsubscribe {
  private readonly router = inject(Router);
  private readonly careerService = inject(CareerService);

  private handleResponseMessage = handleResponseMessage();

  protected readonly loading = signal<boolean>(false);
  protected readonly openPositions = signal<any | null>(null);

  ngOnInit() {
    this.getPositionsData();
  }

  private getPositionsData() {
    this.loading.set(true);

    this.careerService
      .getOpenedPositions()
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: (response: ResponseModel<ICareerOfferModel[]>) => {
          if (response.status && response.data) {
            this.openPositions.set(this.groupPositionsByType(response.data));
          } else {
            this.handleResponseMessage('Failed to get positions', RESPONSE_TYPE.ERROR);
          }
        },
      });
  }

  private groupPositionsByType(data: ICareerOfferModel[]) {
    const groupedMap: Record<string, any> = {};

    data.forEach((item, index) => {
      const type = item.type || 'NotSet';
      const title = type === 'NotSet' ? 'General' : type;

      if (!groupedMap[type]) {
        groupedMap[type] = {
          title: title,
          highlight: `Open positions within our ${item.department.name} that you can apply for right now!`,
          description: "If you don't see a position that suits you, send us your CV",
          positions: [],
        };
      }

      groupedMap[type].positions.push({
        id: item.id || index,
        title: item.name,
        headerBadges: [
          { title: type, color: colorMap[type] || '#6B7280' },
          { title: `${item.level}-Level`, color: colorMap[item.level] || '#6B7280' },
        ],
        description: item.description,
        footerBadges: [
          { title: item.schedule, icon: 'clock-3' },
          { title: item.location, icon: 'briefcase' },
        ],
      });
    });

    return Object.values(groupedMap).sort((a, b) => {
      return (orderMap[a.title] ?? 99) - (orderMap[b.title] ?? 99);
    });
  }

  protected goToJob(position: any) {
    this.router.navigate([PAGE_ROUTES.CAREERS, position.id]);
  }

  protected redirectToEmail(title: string) {
    const email = 'office@sapec.md';
    const subject = `${title} hiring application`;
    const encodedSubject = encodeURIComponent(subject);

    window.location.href = `mailto:${email}?subject=${encodedSubject}`;
  }
}
